// src/oura/delivery/listModel.ts
//
// Which deliverables the list shows, in which group. Later holds only the
// next month; By month shows the rest.

import type { Deliverable, DeliveryGroup } from "@service/aeo/delivery";
import { dayLabel, todayRank } from "@service/aeo/delivery";
import { workingDaysBetween } from "@service/aeo/clientDocsRules";
import { todayIso } from "../components/plan/planDates";
import { plural } from "./format";
import { createDoc, readThenShare } from "@service/aeo/lineWords";

export type ListMode = "next" | "checklist";

export interface DeliveryGroups {
    key: DeliveryGroup;
    rows: Deliverable[];
    /** Folded: shown on the head so a folded group never hides what someone must act on. */
    note?: string;
}

/** The groups the list shows, in order. Later holds only the next month; By month shows the rest. */
export function groupRows(deliverables: readonly Deliverable[], month: number): DeliveryGroups[] {
    const by = (g: DeliveryGroup) => deliverables.filter((d) => d.group === g);
    // Open groups follow the rule every screen uses: came back, a client reply, due soonest, plan order.
    const planIndex = new Map(deliverables.map((d, i): [string, number] => [d.key, i]));
    const ranked = (rows: Deliverable[]) => rows.sort((a, b) => todayRank(a, planIndex.get(a.key) ?? 99) - todayRank(b, planIndex.get(b.key) ?? 99));
    const inView = (d: Deliverable) => d.month <= month || d.group !== "done";
    // In the order the list shows: by due band, then by job, so the drawer's
    // previous and next walk the rows a job moves before leaving it.
    const needs = dueBands(jobsOf(ranked(by("needs_us").filter(inView))), month).flatMap((b) => b.jobs.flatMap((j) => j.rows));
    const waiting = ranked(by("waiting_client"));
    const checking = ranked(by("checking"));
    // This month's, so the group matches the "N of M done" beside it and a monthly code is listed once.
    const done = by("done").filter((d) => d.month === month && !d.reportedByHand);
    const nextMonth = Math.min(...deliverables.filter((d) => d.group === "later").map((d) => d.month).concat([99]));
    const later = by("later").filter((d) => d.month === nextMonth);
    const codes = (rows: Deliverable[]) => rows.map((d) => d.code).join(" · ");
    const laterStart = later[0]?.window?.start;
    return [
        { key: "needs_us" as const, rows: needs },
        { key: "waiting_client" as const, rows: waiting },
        { key: "checking" as const, rows: checking, note: checking.length ? `${codes(checking)} · Re-check tonight` : undefined },
        { key: "done" as const, rows: done, note: done.length ? codes(done) : undefined },
        { key: "later" as const, rows: later, note: later.length ? `${later[0].code} to ${later[later.length - 1].code} · From ${dayLabel(laterStart)}` : undefined },
    ].filter((g) => g.rows.length > 0);
}


/**
 * The step a Needs-us row says. A doc not made or not shared is the step in
 * any open group, by the action's own data, never its words (Jack, #3964):
 * `docId` is what DeliverableSheet already branches on.
 */
export function nextLine(d: Deliverable): string {
    if (d.next) return d.next.text;
    if (d.mainAction.kind === "create_doc") {
        const docType = d.docStep?.docType ?? "client doc";
        return d.mainAction.docId ? readThenShare(docType) : createDoc(docType);
    }
    if (d.group === "waiting_client") {
        const it = d.items.find((i) => i.state === "waiting" && i.waitingOn === "client");
        return it?.nextText ?? it?.clientAsk ?? "Waiting on client";
    }
    if (d.group === "checking") return "Oura re-checks what was marked fixed";
    return d.head;
}

/**
 * One thing to do, and the deliverables it moves. Sixteen Needs-us rows for
 * Brickhouse were eight pieces of work (Jack, 22 Oct 2026): "Create the
 * Answer Plan" sat under B1 and S3, "Read the GBP Pack, then share it" under
 * S4 and each S2 outlet. A document is one job from create to share; any
 * other step is one job per distinct step text.
 */
export interface Job {
    key: string;
    kind: "doc" | "work";
    /** The verb line: "Create the Answer Plan, then share it". */
    title: string;
    /** The deliverables this moves, in list order; the first is the one a click opens. */
    rows: Deliverable[];
}

export function jobsOf(rows: readonly Deliverable[]): Job[] {
    const order: string[] = [];
    // A made document is one job by its id; one not made yet has no id, so its
    // month, type and outlet stand in (two months' Monthly Updates stay apart).
    const keyOf = (d: Deliverable): string => {
        if (d.passedWithoutWork) return `proof:${d.key}`;
        if (d.docStep) {
            const docId = d.docStep.docId ?? (d.mainAction.kind === "create_doc" ? d.mainAction.docId : null);
            return docId ? `doc:id:${docId}` : `doc:pending:${JSON.stringify([d.month, d.docStep.docType, d.docStep.outlet ?? ""])}`;
        }
        // There is no shared-work id for a step: the server gives each item its
        // step by template (DeliveryItem.nextText), so two plan lines carrying
        // the same words, in the same month, outlet and due date, are one piece
        // of work by design (Jack, 22 Oct 2026: "Run the re-score" under two
        // codes is done once). The due date and the person keep rows apart when
        // they differ, so a band head never claims a date or a name that is not
        // true of every row under it. Encoded as a list, not joined with a
        // separator the words could hold.
        return `step:${JSON.stringify([d.month, d.outlet ?? "", d.due ?? "", d.actor?.name ?? "", nextLine(d)])}`;
    };
    // A Map, not an object: the step text comes from the server, and a text that
    // happens to be an inherited property name ("constructor") must not throw.
    const by = new Map<string, Deliverable[]>();
    for (const d of rows) {
        const k = keyOf(d);
        const have = by.get(k);
        if (have) have.push(d);
        else {
            order.push(k);
            by.set(k, [d]);
        }
    }
    return order.map((key) => {
        const rs = by.get(key)!;
        const d = rs[0];
        if (key.startsWith("doc:") && d.docStep) {
            const step = d.docStep;
            const name = step.outlet ? `${step.docType}, ${step.outlet}` : step.docType;
            const create = rs.some((r) => r.docStep?.kind === "create" || (r.mainAction.kind === "create_doc" && !r.mainAction.docId));
            return { key, kind: "doc" as const, title: create ? createDoc(name, true) : readThenShare(name), rows: rs };
        }
        return { key, kind: "work" as const, title: nextLine(d), rows: rs };
    });
}

/**
 * Jobs, split by when they are due. Came back first, then late, needs proof,
 * today, each later date, undated. What every job of a band shares (the
 * person, the date, the month) is said once on its head, so the rows keep
 * only the work and what it moves.
 */
export interface DueBand {
    key: string;
    tone: "late" | "proof" | "today" | "coming";
    title: string;
    /** Late: the shared due date, for hover (the title says how late, as the rows did; Jack, 22 Oct 2026). */
    due: string | null;
    /** The person every row shares, else null and each row names its own. */
    owner: string | null;
    /** The plan month every row shares, when it is not this month. */
    month: number | null;
    jobs: Job[];
}

/**
 * The date a deliverable is due by. Where the tracker's tasks decide Done (#4682), the server sends
 * `dueOn` (the latest task Target), which wins, an explicit null included; absent, the plan's `due`.
 */
export function dueOf(d: Deliverable): string | null {
    return d.dueOn === undefined ? d.due : d.dueOn;
}

export function dueBands(jobs: readonly Job[], month: number): DueBand[] {
    const order: string[] = [];
    const bands: Record<string, Job[]> = {};
    const titles: Record<string, string> = {};
    const head = (d: Deliverable): string => {
        const a = d.actor;
        if (d.passedWithoutWork) return "Needs proof";
        if (a?.cameBack) return "Came back";
        const due = dueOf(d);
        if (a?.late) return due ? `${plural(Math.max(1, workingDaysBetween(due, todayIso())), "day")} late` : "Late";
        // A tracker due date from the server places the deliverable even with no actor (Jack, #4687).
        if (d.dueOn !== undefined) return !due ? "No date yet" : due === todayIso() ? "Due today" : `Due ${dayLabel(due)}`;
        if (!a) return "No date yet";
        return a.when === "today" ? "Due today" : `Due ${a.when}`;
    };
    for (const j of jobs) {
        const d = j.rows[0];
        const title = head(d);
        // Keyed by the date itself, not its words: two came-back jobs due on
        // different days, or two late dates the same number of working days
        // ago, are separate bands, so the head's date is true of every row.
        const k = d.passedWithoutWork || !dueOf(d) ? title : `${title}:${dueOf(d)}`;
        if (!bands[k]) {
            order.push(k);
            titles[k] = title;
        }
        bands[k] = [...(bands[k] ?? []), j];
    }
    const rank = (title: string) => (title === "Came back" ? 0 : title.endsWith("late") || title === "Late" ? 1 : title === "Needs proof" ? 2 : title === "Due today" ? 3 : title === "No date yet" ? 5 : 4);
    order.sort((a, b) => rank(titles[a]) - rank(titles[b]));
    return order.map((key) => {
        const title = titles[key];
        const js = bands[key];
        const rs = js.flatMap((j) => j.rows);
        const names = rs.map((d) => d.actor?.name ?? null).filter((n, i, all) => all.indexOf(n) === i);
        const months = rs.map((d) => d.month).filter((m, i, all) => all.indexOf(m) === i);
        const late = rs.some((d) => d.actor?.late || d.actor?.cameBack);
        return {
            key,
            tone: late ? "late" : title === "Needs proof" ? "proof" : title === "Due today" ? "today" : "coming",
            title,
            due: late ? dueOf(js[0].rows[0]) : null,
            owner: names.length === 1 ? names[0] : null,
            month: months.length === 1 && rs[0].month !== month ? rs[0].month : null,
            jobs: js,
        };
    });
}
